import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { BillHospOrdersSummaryDTO } from '../../dto/budget/BillHospOrdersSummaryDTO';
import { getBenefitOrdersByBudget } from '../../servicesHooks/BillHospitalization/useBillHospitalizationService';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';
import { formatMoney } from '../../utils/money';
import BillHospOrderSection from './BillHospOrderSection';
import BenefitOrderPdfDialog from './BenefitOrderPdfDialog';
import NvTooltips from '../../components/novarium/NvTooltips';
import './billHospitalization/novariumBill.scss';

interface BillHospitalizationOrdersViewProps {
    budgetId: number;
    onBack: () => void;
}

/**
 * Órdenes de prestación de una facturación sanatorial (design system Novarium): qué se generó, quién emite, a quién va
 * y cuánto paga la financiadora y cuánto el paciente. Se abre al generar la orden y desde el historial.
 * Los montos vienen calculados del backend.
 */
export default function BillHospitalizationOrdersView({ budgetId, onBack }: BillHospitalizationOrdersViewProps) {
    const notify = useNotify();
    const [summary, setSummary] = useState<BillHospOrdersSummaryDTO | null>(null);
    const [loading, setLoading] = useState(true);
    // Orden cuyo PDF se está por descargar: el diálogo pregunta qué valores mostrar
    const [pdfOrderId, setPdfOrderId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getBenefitOrdersByBudget(budgetId)
            .then(data => { if (!cancelled) setSummary(data); })
            .catch((error: unknown) => {
                const apiError = parseApiError(error);
                const isBusinessError = apiError.status >= 400 && apiError.status < 500;
                if (!cancelled) notify.error(isBusinessError ? apiError.message : 'No se pudieron cargar las órdenes. Intentá de nuevo.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [budgetId, notify]);

    const subtitle = summary?.patientName
        ? `${summary.patientName}${summary.patientDocument ? ` · DNI ${summary.patientDocument}` : ''}`
        : '';

    return (
        <div className="nv-scope nv-page">
            <NvTooltips />

            <header className="nv-page-head">
                <Button type="button" link icon="pi pi-angle-double-left" label="Volver" className="nv-back" onClick={onBack} />
                <h1 className="nv-h1">Órdenes generadas</h1>
                {subtitle && <span className="nv-subtitle">{subtitle}</span>}
            </header>

            {loading && (
                <div className="nv-loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 32, height: 32 }} />
                    <span>Cargando órdenes…</span>
                </div>
            )}

            {!loading && summary && (
                <>
                    <section className="nv-card" aria-labelledby="nv-orders-totals">
                        <h2 className="nv-section-title" id="nv-orders-totals">Totales</h2>
                        <dl className="nv-summary-grid">
                            <div className="nv-ro">
                                <dt className="nv-ro-label">Financiadora</dt>
                                <dd className="nv-ro-value nv-strong">{formatMoney(summary.totalHealthEntity)}</dd>
                            </div>
                            <div className="nv-ro">
                                <dt className="nv-ro-label">Paciente</dt>
                                <dd className="nv-ro-value nv-strong">{formatMoney(summary.totalPatient)}</dd>
                            </div>
                        </dl>
                        <hr className="nv-divider" />
                        <div className="nv-summary-total">
                            <span className="nv-section-title">Total</span>
                            <span className="nv-kpi">{formatMoney(summary.total)}</span>
                        </div>
                        <p className="nv-help">Las órdenes anuladas no suman en los totales.</p>
                    </section>

                    {summary.orders.length === 0
                        ? <p className="nv-help">La facturación no tiene órdenes generadas.</p>
                        : summary.orders.map(order => <BillHospOrderSection key={order.benefitOrder} order={order} onDownloadPdf={setPdfOrderId} />)}
                </>
            )}

            <BenefitOrderPdfDialog budgetId={budgetId} benefitOrderId={pdfOrderId} onHide={() => setPdfOrderId(null)} />
        </div>
    );
}
